import { ReactElement } from 'react'
import { formatCents, KpiCard, salesReportIcons } from '../../../components/SalesReport'
import { AccessReportResponse } from '../../../hooks/access-report'
import { currentPace, formatAttendance, formatClock, formatCount, formatDayMonth, formatDayTime, formatRate, isLive, localOf, minuteOf } from '../report'
import { FlowChart, HourlyChart } from './charts'
import { DetailSection } from './detail'
import { AttendanceBand, DevicesSection, OriginBreakdown, SectorHeatmap } from './sections'
import * as S from './styles'

interface ReportContentProps {
  report: AccessReportResponse
  mobile: boolean
}

const KPI_COMPACT_LENGTH = 12
export const VALUES_HINT = 'Valores financeiros disponíveis com a permissão Ver valores nos acessos.'

function Kpis({ report, mobile }: ReportContentProps): ReactElement {
  const { kpis, showValues, series, meta } = report
  const live = isLive(report)
  const icon = (src: string): string | undefined => (mobile ? undefined : src)

  const entries = formatCount(kpis.entries)
  const peak = kpis.peakMinute
  const peakValue = live ? `${formatRate(currentPace(series, meta.now))}/min` : peak === null ? '—' : `${formatCount(peak.entries)}/min`
  const peakCaption = live
    ? peak === null
      ? 'sem leituras ainda'
      : `média dos últimos 5 min · pico ${formatCount(peak.entries)}/min às ${formatClock(peak.at)}`
    : peak === null
      ? 'sem leituras'
      : `às ${formatDayTime(peak.at)}`
  const hour = kpis.peakHour
  const hourValue = hour === null ? '—' : `${hour.start.slice(11, 13)}h–${localOf(minuteOf(hour.start) + 60).slice(11, 13)}h`
  const hourCaption =
    hour === null
      ? 'sem leituras'
      : `${formatCount(hour.entries)} entradas · ${formatAttendance(hour.share)} do total · ${formatDayMonth(hour.start)}`
  const revenue = showValues && kpis.revenueCents !== undefined ? formatCents(kpis.revenueCents) : null
  const compact = [entries, peakValue, hourValue, revenue ?? ''].some((value) => value.length > KPI_COMPACT_LENGTH)

  return (
    <S.Kpis>
      <KpiCard
        icon={icon(salesReportIcons.check)}
        label="Entradas validadas"
        value={entries}
        caption={kpis.issued === null ? 'leituras válidas no filtro' : `de ${formatCount(kpis.issued)} ingressos emitidos`}
        compact={compact}
      />
      <KpiCard
        icon={icon(salesReportIcons.chartLine)}
        label={live ? 'Ritmo agora' : 'Pico por minuto'}
        value={peakValue}
        caption={peakCaption}
        compact={compact}
      />
      <KpiCard icon={icon(salesReportIcons.calendarDays)} label="Hora de pico" value={hourValue} caption={hourCaption} compact={compact} />
      {revenue !== null && (
        <KpiCard
          icon={icon(salesReportIcons.ticket)}
          label="Receita dos acessos"
          value={revenue}
          caption={
            kpis.issuedRevenueCents == null
              ? 'em ingressos lidos no filtro'
              : `de ${formatCents(kpis.issuedRevenueCents)} em ingressos emitidos`
          }
          tone="positive"
          compact={compact}
        />
      )}
    </S.Kpis>
  )
}

export function ReportContent({ report, mobile }: ReportContentProps): ReactElement {
  return (
    <S.Content aria-label="Relatório de acessos">
      {isLive(report) && (
        <S.LiveBar role="status">
          <S.LiveDot aria-hidden="true" />
          {`Ao vivo · atualizado às ${report.meta.now.slice(11, 19)}`}
        </S.LiveBar>
      )}
      <Kpis report={report} mobile={mobile} />
      {!report.showValues && <S.Footnote>{VALUES_HINT}</S.Footnote>}
      <AttendanceBand report={report} />
      <FlowChart series={report.series} issued={report.totals.issued} />
      <S.TwoColumns>
        <HourlyChart series={report.series} />
        <OriginBreakdown report={report} />
      </S.TwoColumns>
      <SectorHeatmap heatmap={report.heatmap} />
      <DevicesSection report={report} mobile={mobile} />
      <DetailSection report={report} mobile={mobile} />
    </S.Content>
  )
}
